import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { userService, authService } from '../services/api';
import LanguageSelector from '../components/LanguageSelector';
import PasswordChangeModal from '../components/PasswordChangeModal';
import Logo from '../components/Logo';

/**
 * Pantalla d'entrada.
 *
 * Qui hi arriba ja sap què hi ve a fer: l'hi ha dit el seu instructor. No cal
 * convèncer ningú de res, i per això aquí no hi ha ni eslògans ni llista de
 * funcionalitats. Abans hi havia tres reclams en anglès sobre «defense and
 * security operations» i un bloc amb els comptes de demostració i les seves
 * contrasenyes, servit en obert.
 *
 * Tot el text passa per t(): la pantalla anterior tenia sis crides i la resta
 * era anglès clavat al codi, o sigui que en castellà i en anglès es veia igual.
 */

const camp =
  'w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 ' +
  'placeholder-neutral-400 focus:border-accent-600 focus:outline-none focus:ring-2 ' +
  'focus:ring-accent-600/20 disabled:bg-neutral-50';

const etiqueta = 'mb-1.5 block text-xs font-semibold text-neutral-600';

const Login = () => {
  const { t } = useTranslation();
  const { currentUser, login } = useAuth();
  const [mode, setMode] = useState('login');           // 'login' | 'register'
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [userForPasswordChange, setUserForPasswordChange] = useState(null);

  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({
    full_name: '', username: '', email: '', organization: '',
    password: '', confirmPassword: ''
  });

  if (currentUser && !currentUser.must_change_password) {
    return <Navigate to="/dashboard" />;
  }

  const onLoginChange = (e) => {
    setError(null);
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const onRegisterChange = (e) => {
    setError(null);
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });
  };

  const canviaMode = (nou) => {
    setMode(nou);
    setError(null);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await login(loginData.username, loginData.password);
      if (response.user.must_change_password) {
        setUserForPasswordChange(response.user);
        setShowPasswordChange(true);
        toast.info(t('login.changePasswordRequired'));
      }
    } catch (err) {
      // El backend respon amb codis, no amb frases, justament perquè el text
      // el pugui triar aquí i existeixi en els tres idiomes.
      const codi = err.response?.data?.detail;
      if (codi === 'account_not_active') {
        setError(t('login.errorNotActive'));
      } else if (codi === 'invalid_credentials') {
        setError(t('login.errorCredentials'));
      } else {
        setError(t('login.errorUnknown'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);

    const { full_name, username, email, organization, password, confirmPassword } = registerData;
    if (!full_name || !username || !email || !organization || !password) {
      setError(t('register.errorRequired'));
      return;
    }
    if (password.length < 8) {
      setError(t('register.errorShort'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('register.errorMismatch'));
      return;
    }

    setLoading(true);
    try {
      await authService.register({ full_name, username, email, organization, password });
      setRegistered(true);
    } catch (err) {
      const codi = err.response?.data?.detail;
      setError(codi === 'username_or_email_taken'
        ? t('register.errorTaken')
        : t('register.errorUnknown'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (userId, passwordData) => {
    try {
      await userService.changePassword(userId, passwordData);
      toast.success(t('auth.passwordChanged'));
      setShowPasswordChange(false);
      setUserForPasswordChange(null);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(t('auth.passwordChangeError'));
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 px-4 py-10">
      <div className="absolute right-4 top-4">
        <LanguageSelector />
      </div>

      <div className="mx-auto flex min-h-[80vh] max-w-sm flex-col justify-center">

        {/* Marca */}
        <div className="mb-8 flex items-center gap-3">
          <Logo size={34} className="text-accent-700" />
          <div>
            <h1 className="text-lg font-bold tracking-[0.18em] text-neutral-900">Lucifer</h1>
            <p className="text-xs text-neutral-500">{t('login.subtitle')}</p>
          </div>
        </div>

        {registered ? (
          /* ------- Alta feta, esperant l'administrador ------- */
          <div>
            <h2 className="mb-2 text-base font-semibold text-neutral-900">
              {t('register.doneTitle')}
            </h2>
            <p className="mb-6 text-sm leading-relaxed text-neutral-600">
              {t('register.doneBody')}
            </p>
            <button
              type="button"
              onClick={() => { setRegistered(false); canviaMode('login'); }}
              className="text-sm font-medium text-accent-700 hover:text-accent-800 hover:underline"
            >
              {t('register.backToLogin')}
            </button>
          </div>
        ) : mode === 'login' ? (
          /* ------- Entrar ------- */
          <form onSubmit={handleLogin} noValidate>
            <div className="mb-3">
              <label htmlFor="username" className={etiqueta}>{t('login.username')}</label>
              <input
                id="username" name="username" type="text" autoComplete="username" autoFocus
                value={loginData.username} onChange={onLoginChange}
                className={camp} placeholder={t('login.usernamePlaceholder')}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="password" className={etiqueta}>{t('login.password')}</label>
              <input
                id="password" name="password" type="password" autoComplete="current-password"
                value={loginData.password} onChange={onLoginChange}
                className={camp} placeholder="••••••••"
              />
            </div>

            {error && (
              <p role="alert" className="mb-4 rounded-md border border-error-light bg-error-light/40 px-3 py-2 text-sm text-error-dark">
                {error}
              </p>
            )}

            <button
              type="submit" disabled={loading}
              className="w-full rounded-md bg-accent-700 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-800 focus:outline-none focus:ring-2 focus:ring-accent-600/40 disabled:opacity-60"
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>

            <div className="mt-6 border-t border-neutral-200 pt-4 text-xs leading-relaxed text-neutral-500">
              {t('login.accountsNote')}{' '}
              <button
                type="button" onClick={() => canviaMode('register')}
                className="font-medium text-accent-700 hover:text-accent-800 hover:underline"
              >
                {t('login.requestAccount')}
              </button>
            </div>
          </form>
        ) : (
          /* ------- Demanar un compte ------- */
          <form onSubmit={handleRegister} noValidate>
            <p className="mb-5 text-xs leading-relaxed text-neutral-500">
              {t('register.intro')}
            </p>

            <div className="mb-3">
              <label htmlFor="full_name" className={etiqueta}>{t('register.fullName')}</label>
              <input id="full_name" name="full_name" type="text" autoComplete="name" autoFocus
                value={registerData.full_name} onChange={onRegisterChange} className={camp} />
            </div>
            <div className="mb-3">
              <label htmlFor="reg-username" className={etiqueta}>{t('login.username')}</label>
              <input id="reg-username" name="username" type="text" autoComplete="username"
                value={registerData.username} onChange={onRegisterChange} className={camp} />
            </div>
            <div className="mb-3">
              <label htmlFor="email" className={etiqueta}>{t('register.email')}</label>
              <input id="email" name="email" type="email" autoComplete="email"
                value={registerData.email} onChange={onRegisterChange} className={camp} />
            </div>
            <div className="mb-3">
              <label htmlFor="organization" className={etiqueta}>{t('register.organization')}</label>
              <input id="organization" name="organization" type="text" autoComplete="organization"
                value={registerData.organization} onChange={onRegisterChange} className={camp}
                placeholder={t('register.organizationPlaceholder')} />
            </div>
            <div className="mb-3">
              <label htmlFor="reg-password" className={etiqueta}>{t('login.password')}</label>
              <input id="reg-password" name="password" type="password" autoComplete="new-password"
                value={registerData.password} onChange={onRegisterChange} className={camp} />
              <p className="mt-1 text-xs text-neutral-500">{t('register.passwordHint')}</p>
            </div>
            <div className="mb-4">
              <label htmlFor="confirmPassword" className={etiqueta}>{t('register.confirmPassword')}</label>
              <input id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password"
                value={registerData.confirmPassword} onChange={onRegisterChange} className={camp} />
            </div>

            {error && (
              <p role="alert" className="mb-4 rounded-md border border-error-light bg-error-light/40 px-3 py-2 text-sm text-error-dark">
                {error}
              </p>
            )}

            <button
              type="submit" disabled={loading}
              className="w-full rounded-md bg-accent-700 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-800 focus:outline-none focus:ring-2 focus:ring-accent-600/40 disabled:opacity-60"
            >
              {loading ? t('register.sending') : t('register.submit')}
            </button>

            <div className="mt-6 border-t border-neutral-200 pt-4 text-xs text-neutral-500">
              <button
                type="button" onClick={() => canviaMode('login')}
                className="font-medium text-accent-700 hover:text-accent-800 hover:underline"
              >
                {t('register.backToLogin')}
              </button>
            </div>
          </form>
        )}

        <p className="mt-10 text-xs text-neutral-400">
          {t('layout.aProjectOf')}{' '}
          <a href="https://strategos.cat" target="_blank" rel="noopener noreferrer"
             className="text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-accent-700">
            Strategos.cat
          </a>
        </p>
      </div>

      {showPasswordChange && userForPasswordChange && (
        <PasswordChangeModal
          user={userForPasswordChange}
          onClose={() => setShowPasswordChange(false)}
          onSubmit={handlePasswordChange}
          /* No és cap restabliment d'administrador: és l'usuari canviant-se la
             seva contrasenya el primer cop que entra, i per tant l'ha de saber
             —l'acaba d'escriure per entrar—. */
          isAdminReset={false}
        />
      )}
    </div>
  );
};

export default Login;
